import { Checkbox, Form, Input, Radio, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import styles from 'src/app/pages/PortalPage/ConfigSettings/ConfigSettings.module.scss';
import {
  getConfigSettings,
  updateAndCreateConfigSettings,
} from 'src/app/services/config-settings/config-settings.service';
import { sensorCategoriesOfTypes } from 'src/configs/interests.config';
import { systemConfigSetter } from 'src/configs/system.config';
import { InterestsTypes } from 'src/enums/interests.enum';
import { ReportTypes } from 'src/enums/logger-export.enum';
import { FileUploadTypes, SensorCategoryType, SensorType } from 'src/enums/nginr-type.enum';
import { MediaType } from 'src/enums/surfer.enums';
import { LayoutViewType } from 'src/enums/user.enum';
import { SaveIcon } from 'src/icons';

const defaultFormOptions = {
  checkBoxOptions: {
    SensorCategoryType: Object.keys(SensorCategoryType).filter((key) => Number.isNaN(Number(key))),
    MediaType: Object.keys(MediaType).filter((key) => Number.isNaN(Number(key))),
    SensorType: Object.keys(SensorType).filter((key) => Number.isNaN(Number(key))),
    InterestsTypes: Object.keys(InterestsTypes).filter((key) => Number.isNaN(Number(key))),
    ReportTypes: Object.keys(ReportTypes).filter((key) => Number.isNaN(Number(key))),
    FileUploadTypes: Object.keys(FileUploadTypes).filter((key) => Number.isNaN(Number(key))),
    SystemLoginStatus: 'Not Active',
  },
  urlOptions: {
    sensorUrls: {
      NewsSiteUrls: ['https://www.nytimes.com/', 'https://bbc.com/'],
      BlacklistUrls: ['https://www.blacklisttest.com'],
      CourtRecordUrls: ['https://www.courtRecord.com', 'courtRecord2.com'],
      SocialMediaUrls: ['Instagram', 'X', 'Facebook'],
      WebSiteUrls: ['website1.com', 'website2.com', 'website3.com'],
    },
  },
  stringOptions: {
    LandUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    SatelliteUrl: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  },
  radioButtonOption: {
    WebLayoutDesignType: Object.keys(LayoutViewType).filter((key) => Number.isNaN(Number(key))),
  },
};

export default function ConfigSettings() {
  const [form] = useForm();
  const [systemloginStatus, setSystemloginStatus] = useState<boolean>(false);

  const onFinish = async (valuesConfig: any) => {
    const getByEnumKey = (enumValue: any, values: any) => {
      const getKey = Object.entries(enumValue)
        .filter((key) => values.includes(key[1]))
        .map((o) => Number(o[0]));

      return getKey;
    };

    const dto = {
      sensorCategoryTypes: getByEnumKey(
        SensorCategoryType,
        valuesConfig.SensorCategoryType.map((item: string) => item.replaceAll(' ', '_')),
      ),
      mediaTypes: getByEnumKey(
        MediaType,
        valuesConfig.MediaType.map((item: string) => item.replaceAll(' ', '_')),
      ),
      sensorTypes: getByEnumKey(
        SensorType,
        valuesConfig.SensorType.map((item: string) => item.replaceAll(' ', '_')),
      ),
      interestTypes: getByEnumKey(
        InterestsTypes,
        valuesConfig.InterestsTypes.map((item: string) => item.replaceAll(' ', '_')),
      ),
      reportTypes: getByEnumKey(
        ReportTypes,
        valuesConfig.ReportTypes.map((item: string) => item.replaceAll(' ', '_')),
      ),
      fileUploadTypes: getByEnumKey(
        FileUploadTypes,
        valuesConfig.FileUploadTypes.map((item: string) => item.replaceAll(' ', '_')),
      ),
      systemLoginStatus: valuesConfig.SystemLoginStatus.length === 0 ? 'Not Active' : valuesConfig.SystemLoginStatus[0],
      webLayoutDesignType: LayoutViewType[valuesConfig.WebLayoutDesignType.replaceAll(' ', '_')],
      newsSiteUrls: valuesConfig.NewsSiteUrls,
      blacklistUrls: valuesConfig.BlacklistUrls,
      courtRecordUrls: valuesConfig.CourtRecordUrls,
      socialMediaUrls: valuesConfig.SocialMediaUrls,
      webSiteUrls: valuesConfig.WebSiteUrls,
      landUrl: valuesConfig.LandUrl,
      satelliteUrl: valuesConfig.SatelliteUrl,
    };

    await updateAndCreateConfigSettings(dto);
    await getData();
  };

  const checkBoxOptionKeys = defaultFormOptions.checkBoxOptions;
  const urlOptionMainKeys = Object.keys(defaultFormOptions.urlOptions);
  const stringOptionKeys = Object.keys(defaultFormOptions.stringOptions);
  const urlOptionSubKeys = {
    sensorUrls: [...Object.keys(defaultFormOptions.urlOptions.sensorUrls)],
  };
  const sensorUrlsValues = defaultFormOptions.urlOptions.sensorUrls;

  useEffect(() => {
    try {
      getData();
    } catch (error) {
      console.log('Error: ', error);
    }
  }, []);

  const capitalizeFunc = (str: string) => {
    const newStr = str
      .split('_')
      .map((each) => each.charAt(0) + each.slice(1))
      .join(' ');
    return newStr;
  };

  const splitCapitalFunc = (str: string) => {
    const capitalLetterRegex = /(?=[A-Z])/;
    return str.split(capitalLetterRegex).join(' ');
  };

  const getData = async () => {
    const { data } = await getConfigSettings();

    const updateStatus = (statusData: string) => {
      let updatedData: string;
      if (statusData === 'Active') {
        setSystemloginStatus(true);
        updatedData = statusData;
      } else {
        setSystemloginStatus(false);
        updatedData = '';
      }
      return [updatedData];
    };

    if (!data) {
      form.setFieldsValue(defaultFormOptions);
    } else {
      form.setFieldsValue({
        SensorCategoryType: Object.entries(SensorCategoryType)
          .filter((key) => data.sensorCategoryTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        MediaType: Object.entries(MediaType)
          .filter((key) => data.mediaTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        SensorType: Object.entries(SensorType)
          .filter((key) => data.sensorTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        InterestsTypes: Object.entries(InterestsTypes)
          .filter((key) => data.interestTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        ReportTypes: Object.entries(ReportTypes)
          .filter((key) => data.reportTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        FileUploadTypes: Object.entries(FileUploadTypes)
          .filter((key) => data.fileUploadTypes.includes(Number(key[0])))
          .map((o) => o[1].toString().replaceAll('_', ' ') as string),
        SystemLoginStatus: updateStatus(data.systemLoginStatus),
        WebLayoutDesignType: Object.entries(LayoutViewType)
          .find((key) => data.webLayoutDesignType === Number(key[0]))?.[1]
          .toString()
          .replaceAll('_', ' ') as string,
        NewsSiteUrls: data.newsSiteUrls,
        BlacklistUrls: data.blacklistUrls,
        CourtRecordUrls: data.courtRecordUrls,
        SocialMediaUrls: data.socialMediaUrls,
        WebSiteUrls: data.webSiteUrls,

        LandUrl: data.landUrl,
        SatelliteUrl: data.satelliteUrl,
      });

      systemConfigSetter(data);
    }
  };

  const onCheckboxChange = (category: string, checkedKey: any[]) => {
    const keyss = checkedKey[0];
    if (category === 'SensorCategoryType') {
      const selectedCategory = Object.entries(SensorCategoryType)
        .filter((key) => keyss.includes(key[1].toString().replace('_', ' ')))
        .map((obj) => Number(obj[0]));

      const sensorTypes: any[] = [];
      selectedCategory.forEach((checkkey) =>
        sensorTypes.push(...sensorCategoriesOfTypes[checkkey as SensorCategoryType]),
      );

      const sensorTypeName = Object.entries(SensorType)
        .filter((key) => sensorTypes.includes(Number(key[0])))
        .map((o) => o[1].toString().replace('_', ' ') as string);

      form.setFieldsValue({
        SensorCategoryType: keyss,
        SensorType: sensorTypeName,
      });
    } else if (category === 'SensorType') {
      const sensorCategories = new Set<string>();
      keyss.forEach((item: any) => {
        const sensorTypeNumber = Number(SensorType[item.replaceAll(' ', '_')]);
        const categoryyy = Object.entries(sensorCategoriesOfTypes).filter((obj) => obj[1].includes(sensorTypeNumber));

        sensorCategories.add(SensorCategoryType[Number(categoryyy[0][0])].replaceAll('_', ' '));
      });
      form.setFieldsValue({ SensorCategoryType: [...sensorCategories], SensorType: keyss });
    } else if (category === 'SystemLoginStatus') {
      if (checkedKey[0].length > 0 && checkedKey[0][0] !== 'Active') {
        setSystemloginStatus(true);
        form.setFieldsValue({ SystemLoginStatus: 'Active' });
      } else {
        setSystemloginStatus(false);
        // form.setFieldsValue({ SystemLoginStatus: 'Not Active' });
      }
    }
  };

  return (
    <div>
      <div className={styles.extraTab}>
        <ExtraTab
          onClick={() => {
            showModal({
              title: 'Save',
              content: 'Are you sure to apply all changes?',
              onSave(modal) {
                form.submit();
                modal.destroy();
              },
            });
          }}
          text={t('global.save')}
          icon={<SaveIcon />}
          iconTheme
        />
      </div>
      <Form
        form={form}
        onFinish={onFinish}
        className={styles.form}
        layout="vertical"
        onValuesChange={(changedValues: any) =>
          onCheckboxChange(Object.keys(changedValues)[0], Object.values(changedValues))
        }
      >
        {Object.entries(checkBoxOptionKeys).map((key) => (
          <Form.Item
            key={key[0]}
            className={styles.checkBox}
            name={`${key[0]}`}
            label={<span className={styles.label}>{splitCapitalFunc(key[0])}</span>}
          >
            <Checkbox.Group
              className={styles.checkBoxGroup}
              options={
                typeof key[1] !== 'string'
                  ? key[1].map((item) => capitalizeFunc(item))
                  : systemloginStatus === true
                    ? ['Active']
                    : ['Not Active']
              }
            />
          </Form.Item>
        ))}
        {Object.entries(defaultFormOptions.radioButtonOption).map((key) => (
          <Form.Item
            key={key[0]}
            className={styles.radioBox}
            name={`${key[0]}`}
            label={<span className={styles.label}>{splitCapitalFunc(key[0])}</span>}
          >
            <Radio.Group className={styles.radioButtonsGroup} options={key[1].map((item) => capitalizeFunc(item))} />
          </Form.Item>
        ))}
        <div className={styles.stringBox}>
          {stringOptionKeys.map((key) => (
            <Form.Item name={key} label={<span>{splitCapitalFunc(key)}</span>} key={key}>
              <Input />
            </Form.Item>
          ))}
        </div>
        {urlOptionMainKeys.map((mainKey) => (
          <div key={mainKey} className={styles.urlsBox}>
            {(urlOptionSubKeys as any)[mainKey].map((subKey: string) => (
              <Form.Item name={subKey} label={<span>{splitCapitalFunc(subKey)}</span>} key={subKey}>
                <Select mode="tags">
                  {(sensorUrlsValues as any)[subKey].map((url: string) => (
                    <Select.Option key={url} value={url}>
                      {url}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ))}
          </div>
        ))}
      </Form>
    </div>
  );
}
